import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';

import * as actionCreators from '@src/store/actions';
import * as selectors from '@src/store/selectors';
import { ui } from '@src/services';
import useTranslation from '@src/hooks/useTranslation';
import { getPasswordStrength } from '@src/utils/password';
import { getPasswordPolicyErrorMessageKey } from '@src/utils/passwordPolicy';
import { TOAST_TYPE_ERROR, TOAST_TYPE_SUCCESS } from '@src/const/common';
import { IconComponent } from '@src/icons';
import PasswordStrengthText from '@src/components/PasswordStrengthText';
import ForgetPasswordModal from '@src/components/modalContainer/components/forgetPasswordModal';

const Backdrop = styled.div`
	position: fixed;
	inset: 0;
	z-index: 10000;
	display: flex;
	align-items: center;
	justify-content: center;
	background: rgba(0, 0, 0, 0.5);
	padding: 16px;
`;

const Card = styled.div`
	width: 400px;
	min-height: 431px;
	padding: 26px 24px 32px;
	border: 1px solid #eeeeee;
	border-radius: 2px;
	background: #ffffff;
	box-shadow: 0 1px 15px rgba(69, 65, 78, 0.08);

	@media ${props => props.theme.device.mobile} {
		width: 100%;
		min-height: auto;
	}
`;

const Title = styled.h2`
	margin: 0;
	font-size: 18px;
	font-weight: 500;
	line-height: 22px;
	color: #333333;
	text-align: center;
`;

const Description = styled.div`
	margin-top: 14px;
	font-size: 14px;
	font-weight: 500;
	line-height: 21px;
	color: #222222;
	text-align: center;
	word-break: break-word;
`;

const FormSection = styled.div`
	margin-top: 26px;
`;

const FieldGroup = styled.div`
	& + & {
		margin-top: 25px;
	}
`;

const Label = styled.div`
	margin-bottom: 11px;
	font-size: 16px;
	font-weight: 400;
	line-height: 16px;
	color: #222222;
`;

const InputShell = styled.div`
	position: relative;
`;

const Input = styled.input`
	width: 100%;
	height: 45px;
	padding: 0 48px 0 10px;
	border: 1px solid ${props => (props.$hasError ? '#d0021b' : '#dbdbdb')};
	border-radius: 2px;
	font-size: 16px;
	font-weight: 350;
	line-height: 24px;
	letter-spacing: 0.54px;
	color: #222222;
	outline: none;

	&::placeholder {
		color: #676767;
	}

	&:focus {
		border-color: #3091fd;
	}
`;

const EyeButton = styled.button`
	position: absolute;
	top: 50%;
	right: 10px;
	transform: translateY(-50%);
	display: inline-flex;
	align-items: center;
	justify-content: center;
	padding: 0;
	border: 0;
	background: transparent;
	color: #d2d2d2;
	cursor: pointer;
`;

const Hint = styled.p`
	margin: 6px 0 0;
	font-size: 14px;
	font-weight: 500;
	line-height: 20px;
	color: ${props => props.$color || '#d0021b'};
`;

const SubmitButton = styled.button`
	width: 100%;
	height: 45px;
	margin-top: 44px;
	border: 0;
	border-radius: 2px;
	background: ${props => (props.disabled ? '#9c9c9c' : '#3091fd')};
	font-size: 16px;
	font-weight: 500;
	line-height: 24px;
	color: #ffffff;
	cursor: ${props => (props.disabled ? 'not-allowed' : 'pointer')};

	&:hover {
		background: ${props => (props.disabled ? '#9c9c9c' : '#005abe')};
	}
`;

const ForgotPasswordButton = styled.button`
	display: block;
	margin: 12px auto 0;
	padding: 0;
	border: 0;
	background: transparent;
	font-size: 14px;
	font-weight: 400;
	line-height: 21px;
	color: #3091fd;
	cursor: pointer;
`;

function ForceChangePasswordModal() {
	const dispatch = useDispatch();
	const { t } = useTranslation();
	const profile = useSelector(selectors.getUserProfile) || {};
	const putProfilePasswordSideEffect = useSelector(
		selectors.getPutProfilePasswordSideEffect
	);

	const [password, setPassword] = useState('');
	const [newPassword, setNewPassword] = useState('');
	const [confirmPassword, setConfirmPassword] = useState('');
	const [showCurrentPassword, setShowCurrentPassword] = useState(false);
	const [showNewPassword, setShowNewPassword] = useState(false);
	const [showConfirmPassword, setShowConfirmPassword] = useState(false);
	const [isSubmitted, setIsSubmitted] = useState(false);
	const [currentPasswordErrorKey, setCurrentPasswordErrorKey] = useState('');
	const [newPasswordErrorKey, setNewPasswordErrorKey] = useState('');
	const [isForgetPasswordModalOpen, setIsForgetPasswordModalOpen] = useState(false);

	const passwordStrength = getPasswordStrength(newPassword);
	const isValidationNewPassword = newPassword.length >= 8;
	const isSameNewPassword = newPassword === confirmPassword;
	const isWeakPassword = newPassword.length > 0 && passwordStrength.type === 'weak';
	const canSubmit = password.length > 0 &&
		newPassword.length > 0 &&
		confirmPassword.length > 0 &&
		isValidationNewPassword &&
		isSameNewPassword &&
		!isWeakPassword;

	const strengthMessage = useMemo(() => {
		if (!newPassword) {
			return null;
		}

		return passwordStrength.message;
	}, [newPassword, passwordStrength]);

	const handleSubmit = event => {
		event.preventDefault();
		setIsSubmitted(true);

		if (!canSubmit) {
			return;
		}

		dispatch(
			actionCreators.putProfilePasswordCall({
				form: { password, new_password: newPassword },
				onSuccess: () => {
					setPassword('');
					setNewPassword('');
					setConfirmPassword('');
					setCurrentPasswordErrorKey('');
					setNewPasswordErrorKey('');
					ui.toastShow({
						message: 'share:profile:password:success_title',
						type: TOAST_TYPE_SUCCESS,
					});
				},
				onError: error => {
					const passwordPolicyErrorKey = getPasswordPolicyErrorMessageKey(error?.response?.data?.error_type);

					if (passwordPolicyErrorKey) {
						setNewPasswordErrorKey(passwordPolicyErrorKey);
						return;
					}

					if (error?.response?.status === 400) {
						setNewPasswordErrorKey('share:profile:password:error:same_as_old');
						return;
					}

					if (error?.response?.status === 403) {
						setCurrentPasswordErrorKey('share:profile:password:error:incorrect');
						return;
					}

					ui.toastShow({ message: 'common:message:server_error', type: TOAST_TYPE_ERROR });
				},
			})
		);
	};

	const showNewPasswordMismatchError = isSubmitted && confirmPassword.length > 0 && !isSameNewPassword;
	const showNewPasswordLengthError = isSubmitted && !isValidationNewPassword;

	if (isForgetPasswordModalOpen) {
		return (
			<ForgetPasswordModal
				onClose={() => setIsForgetPasswordModalOpen(false)}
			/>
		);
	}

	return (
		<Backdrop>
			<Card>
				<Title>{t('share:auth:reset_password:title')}</Title>
				<Description>
					<div>{t('share:auth:reset_password:policy:force_change_notice')}</div>
					<div>{profile.email}</div>
				</Description>
				<FormSection as="form" onSubmit={handleSubmit}>
					<FieldGroup>
						<Label>{t('training:account:settings:current_password')}</Label>
						<InputShell>
							<Input
								type={showCurrentPassword ? 'text' : 'password'}
								placeholder={t('training:account:settings:enter_current_password')}
								value={password}
								autoComplete="current-password"
								onChange={event => {
									setCurrentPasswordErrorKey('');
									setPassword(event.target.value);
								}}
								$hasError={!!currentPasswordErrorKey}
							/>
							<EyeButton
								type="button"
								onClick={() => setShowCurrentPassword(!showCurrentPassword)}
							>
								<IconComponent
									size={22}
									color="#dcdcdc"
									icon={showCurrentPassword ? 'login_form_eye_onsvg' : 'login_form_eye_off'}
								/>
							</EyeButton>
						</InputShell>
						{currentPasswordErrorKey && (
							<Hint>{t(currentPasswordErrorKey)}</Hint>
						)}
					</FieldGroup>
					<FieldGroup>
						<Label>{t('share:auth:reset_password:new_password')}</Label>
						<InputShell>
							<Input
								type={showNewPassword ? 'text' : 'password'}
								placeholder={t('common:form:password_placeholder')}
								value={newPassword}
								autoComplete="new-password"
								onChange={event => {
									setNewPasswordErrorKey('');
									setNewPassword(event.target.value);
								}}
								$hasError={!!newPasswordErrorKey || showNewPasswordLengthError || isWeakPassword}
							/>
							<EyeButton
								type="button"
								onClick={() => setShowNewPassword(!showNewPassword)}
							>
								<IconComponent
									size={22}
									color="#dcdcdc"
									icon={showNewPassword ? 'login_form_eye_onsvg' : 'login_form_eye_off'}
								/>
							</EyeButton>
						</InputShell>
						{strengthMessage && (
							<Hint $color={passwordStrength.color}>
								<PasswordStrengthText strength={passwordStrength} />
							</Hint>
						)}
						{isWeakPassword && (
							<Hint>{t('share:auth:reset_password:password_requirement')}</Hint>
						)}
						{showNewPasswordLengthError && !isWeakPassword && (
							<Hint>{t('training:account:settings:enter_at_least')}</Hint>
						)}
						{newPasswordErrorKey && (
							<Hint>{t(newPasswordErrorKey)}</Hint>
						)}
					</FieldGroup>
					<FieldGroup>
						<Label>{t('share:auth:reset_password:re_new_password')}</Label>
						<InputShell>
							<Input
								type={showConfirmPassword ? 'text' : 'password'}
								placeholder={t('common:form:password_placeholder')}
								value={confirmPassword}
								autoComplete="new-password"
								onChange={event => {
									setNewPasswordErrorKey('');
									setConfirmPassword(event.target.value);
								}}
								$hasError={showNewPasswordMismatchError}
							/>
							<EyeButton
								type="button"
								onClick={() => setShowConfirmPassword(!showConfirmPassword)}
							>
								<IconComponent
									size={22}
									color="#dcdcdc"
									icon={showConfirmPassword ? 'login_form_eye_onsvg' : 'login_form_eye_off'}
								/>
							</EyeButton>
						</InputShell>
						{showNewPasswordMismatchError && (
							<Hint>{t('common:message:password_mismatch')}</Hint>
						)}
					</FieldGroup>
					<SubmitButton
						type="submit"
						disabled={!canSubmit || putProfilePasswordSideEffect?.isLoading}
					>
						{t('share:auth:reset_password:send')}
					</SubmitButton>
					<ForgotPasswordButton
						type="button"
						onClick={() => setIsForgetPasswordModalOpen(true)}
					>
						{t('share:auth:login:forget')}
					</ForgotPasswordButton>
				</FormSection>
			</Card>
		</Backdrop>
	);
}

export default ForceChangePasswordModal;
